import { catalogChoices, type CatalogChoice } from '@/lib/catalog-choices'
import { ModelDefaultsReview } from '@/components/catalog/ModelDefaultsReview'
import { parseApiErrorMessage } from '@/lib/api-errors'
import { Link } from 'react-router-dom'
import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react'
import { toast } from 'sonner'
import { useCurrentShow } from '@/context/current-show'
import {
  useAddCatalogClassToShow,
  useCatalogClasses,
  useDivisions,
  useUpdateShowClass,
  useUpdateClassFinalization,
  useReindexClassCodes,
  useCatalogClassUsage,
} from '@/api/hooks'
import { CompactShowSummary, SelectShowNotice } from '@/components/current-show'
import {
  ShowClassesOutline,
  type DivisionWithClasses,
  type LabelsEditorState,
} from '@/components/classes/ShowClassesOutline'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { getCategoryBadgeClass } from '@/lib/classColors'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Separator } from '@/components/ui/separator'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import RulesViewer from '@/components/catalog/RulesViewer'
import { mergeCatalogRules, safeJsonParse } from '@/lib/catalog'
import { resolveLabelsPerExhibitFromClass } from '@/lib/labels-per-exhibit'
import { formatRoleList, useActorPermissions } from '@/hooks/useActorPermissions'
import {
  CLASS_PLACEMENT_KEYS,
  DEFAULT_PLACE_COUNT,
  formatPlaceCount,
  formatPlaceRange,
  type AddCatalogClassToShowInput,
  type CatalogClassUsageOutput,
  type TClass,
  type TDivision,
  type UpdateClassFinalizationInput,
} from '@schemas'

const CATALOG_CATEGORY_CODES = ['H', 'W', 'A', 'B', 'F', 'N'] as const
const CATEGORY_ORDER = new Map<string, number>(
  CATALOG_CATEGORY_CODES.map((code, index) => [code, index])
)

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

type CatalogFilters = {
  category?: string
  q?: string
}

type OverrideFormState = {
  displayName: string
  ruleOverrides: string
  parameters: string
}

type BulkFormState = {
  divisionMode: 'existing' | 'new'
  existingDivisionId?: string
  newDivisionCode: string
  newDivisionName: string
  newDivisionOrder: string
  codePrefix: string
  codeStart: string
  namePrefix: string
  nameSuffix: string
}

type BulkAddResult = {
  catalogClassId: string
  catalogClassName: string
  status: 'success' | 'error'
  message?: string
}

type DivisionPlan = {
  divisionId?: string
  divisionCode: string
  divisionName: string
  divisionOrder?: number
}

type CatalogUsageGroup = {
  show: CatalogClassUsageOutput['instances'][number]['show']
  isCurrentShow: boolean
  entries: Array<{
    division: CatalogClassUsageOutput['instances'][number]['division']
    class: CatalogClassUsageOutput['instances'][number]['class']
    isCurrentClass: boolean
  }>
}

const defaultOverrideForm: OverrideFormState = {
  displayName: '',
  ruleOverrides: '',
  parameters: '',
}

const defaultBulkForm: BulkFormState = {
  divisionMode: 'existing',
  existingDivisionId: undefined,
  newDivisionCode: '',
  newDivisionName: '',
  newDivisionOrder: '',
  codePrefix: '',
  codeStart: '1',
  namePrefix: '',
  nameSuffix: '',
}

function formatDivisionLabel(division: TDivision) {
  return `${division.code} — ${division.name}`
}

function generateDivisionCode(divisions: TDivision[]): string {
  const existing = new Set(divisions.map(division => division.code.toUpperCase()))
  let counter = divisions.length + 1
  let code = `DIV-${counter}`
  while (existing.has(code.toUpperCase())) {
    counter += 1
    code = `DIV-${counter}`
  }
  return code
}

function generateDivisionName(divisions: TDivision[], fallback?: string) {
  const existingNames = new Set(divisions.map(division => division.name.trim().toLowerCase()))
  const baseName = fallback?.trim() || `Division ${divisions.length + 1}`
  if (!existingNames.has(baseName.toLowerCase())) return baseName

  let counter = 2
  let candidate = `${baseName} ${counter}`
  while (existingNames.has(candidate.toLowerCase())) {
    counter += 1
    candidate = `${baseName} ${counter}`
  }
  return candidate
}

function formatIsoDate(value: string) {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

function toParametersRecord(input: unknown): Record<string, unknown> {
  if (typeof input === 'string') {
    const parsed = safeJsonParse<unknown>(input, null)
    if (isRecord(parsed)) {
      return { ...parsed }
    }
    return {}
  }

  if (isRecord(input)) {
    return { ...input }
  }

  return {}
}

export default function ShowClassesPage() {
  const { currentShow } = useCurrentShow()
  const showId = currentShow?.id
  const permissions = useActorPermissions(currentShow?.id)
  const reindexMutation = useReindexClassCodes()

  const hasReindexAccess = permissions.can('class:reindex')
  const reindexRolesLabel = useMemo(
    () => formatRoleList(permissions.getAllowedRoles('class:reindex')),
    [permissions]
  )
  const canManageClasses = permissions.can('class:write')
  const classRolesLabel = useMemo(
    () => formatRoleList(permissions.getAllowedRoles('class:write')),
    [permissions]
  )
  const permissionLoading = permissions.staffQuery?.isLoading ?? false
  const classPermissionDisabled = permissionLoading || !canManageClasses
  const classPermissionReason = permissionLoading
    ? 'Loading permissions…'
    : !canManageClasses
      ? `Managing classes requires ${classRolesLabel}.`
      : undefined
  const canFinalizeResults = permissions.can('awards:finalize')
  const resultsRolesLabel = useMemo(
    () => formatRoleList(permissions.getAllowedRoles('awards:finalize')),
    [permissions]
  )
  const resultsPermissionDisabled = permissionLoading || !canFinalizeResults
  const resultsPermissionReason = permissionLoading
    ? 'Loading permissions…'
    : !canFinalizeResults
      ? `Changing class results requires ${resultsRolesLabel}.`
      : undefined
  const showIsDraft = currentShow?.status === 'draft'
  const reindexButtonDisabled =
    !showIsDraft || !showId || reindexMutation.isPending || permissionLoading
  const reindexButtonTitle = !showIsDraft
    ? 'Reindex is only available while the show remains in draft.'
    : undefined

  const handleReindexCodes = useCallback(async () => {
    if (!showId) return
    if (!hasReindexAccess) {
      toast.error(`Reindexing is limited to ${reindexRolesLabel}.`)
      return
    }
    if (!showIsDraft) {
      toast.error('Reindexing is only available while the show is in draft status.')
      return
    }

    const showName = currentShow?.name ?? 'this show'
    if (typeof window !== 'undefined') {
      const confirmed = window.confirm(
        `Reindex class codes for ${showName}? Codes will be reassigned by category letter and sequence within each division.`
      )
      if (!confirmed) return
    }

    try {
      const result = await reindexMutation.mutateAsync({ showId })
      const updatedCount = result.divisions.reduce(
        (total, division) => total + division.updated.length,
        0
      )
      const affectedDivisions = result.divisions.length
      if (updatedCount === 0) {
        toast.success('Class codes already aligned—no changes were needed.')
        return
      }
      const summary = `${updatedCount} class${updatedCount === 1 ? '' : 'es'} across ${affectedDivisions} division${
        affectedDivisions === 1 ? '' : 's'
      }`
      toast.success(`Reindexed ${summary}`)
    } catch (error) {
      toast.error(parseApiErrorMessage(error, 'Unable to reindex class codes'))
    }
  }, [currentShow?.name, hasReindexAccess, reindexMutation, showId, showIsDraft])

  const ensureCanManageClasses = useCallback(() => {
    if (classPermissionDisabled) {
      if (classPermissionReason) {
        toast.error(classPermissionReason)
      }
      return false
    }
    return true
  }, [classPermissionDisabled, classPermissionReason])

  const updateClassFinalization = useUpdateClassFinalization()

  const handleUpdateFinalization = useCallback(
    (classId: string, payload: UpdateClassFinalizationInput) => {
      if (resultsPermissionDisabled) {
        if (resultsPermissionReason) toast.error(resultsPermissionReason)
        return
      }
      updateClassFinalization.mutate(
        { classId, payload, showId: showId ?? undefined },
        {
          onSuccess: updated => {
            toast.success(`Updated finalization for ${updated.code}`)
          },
          onError: error => {
            toast.error(error.message)
          },
        }
      )
    },
    [resultsPermissionDisabled, resultsPermissionReason, updateClassFinalization, showId]
  )

  const [catalogFilters, setCatalogFilters] = useState<CatalogFilters>({})
  const [editSheetOpen, setEditSheetOpen] = useState(false)
  const editTriggerRef = useRef<HTMLButtonElement | null>(null)
  const loadedOverride = useRef<{ id: string | null; values: OverrideFormState }>({
    id: null,
    values: defaultOverrideForm,
  })
  const [selectedClassId, setSelectedClassId] = useState<string | null>(null)
  const [overrideForm, setOverrideForm] = useState<OverrideFormState>(defaultOverrideForm)
  const [overrideError, setOverrideError] = useState<string | null>(null)
  const [bulkSheetOpen, setBulkSheetOpen] = useState(false)
  const [bulkSelected, setBulkSelected] = useState<Map<string, CatalogChoice>>(() => new Map())
  const [bulkForm, setBulkForm] = useState<BulkFormState>(defaultBulkForm)
  const [bulkError, setBulkError] = useState<string | null>(null)
  const [bulkResults, setBulkResults] = useState<BulkAddResult[]>([])
  const [bulkSubmitting, setBulkSubmitting] = useState(false)
  const [labelsEditor, setLabelsEditor] = useState<LabelsEditorState>({})
  const [savingClassId, setSavingClassId] = useState<string | null>(null)

  const overrideFieldId = useId()
  const bulkFormId = useId()

  const overrideFieldIds = {
    displayName: `${overrideFieldId}-display-name`,
    ruleOverrides: `${overrideFieldId}-rule-overrides`,
    parameters: `${overrideFieldId}-parameters`,
  }

  const bulkFormIds = {
    existingDivision: `${bulkFormId}-existing-division`,
    newDivisionCode: `${bulkFormId}-new-division-code`,
    newDivisionName: `${bulkFormId}-new-division-name`,
    newDivisionOrder: `${bulkFormId}-new-division-order`,
    codePrefix: `${bulkFormId}-code-prefix`,
    codeStart: `${bulkFormId}-code-start`,
    namePrefix: `${bulkFormId}-name-prefix`,
    nameSuffix: `${bulkFormId}-name-suffix`,
  }

  const divisionsQuery = useDivisions(showId)
  const bulkAddMutation = useAddCatalogClassToShow()
  const updateClassMutation = useUpdateShowClass()
  const divisions: DivisionWithClasses[] = useMemo(
    () => divisionsQuery.data ?? [],
    [divisionsQuery.data]
  )

  const handleToggleGrandChampionEligibility = useCallback(
    async (classId: string, nextExcluded: boolean, classLabel: string) => {
      if (!ensureCanManageClasses()) return
      if (!showId) {
        toast.error('Select a show to update Grand Champion eligibility.')
        return
      }
      try {
        await updateClassMutation.mutateAsync({
          classId,
          showId,
          payload: { grandChampionEligible: !nextExcluded },
        })
        toast.success(
          nextExcluded
            ? `${classLabel} excluded from Grand Champion`
            : `${classLabel} eligible for Grand Champion`
        )
      } catch (error) {
        toast.error(
          error instanceof Error ? error.message : 'Unable to update Grand Champion eligibility.'
        )
      }
    },
    [ensureCanManageClasses, showId, updateClassMutation]
  )

  // The choice being saved; the selector shows it until the saved class reaches the cache.
  const [placeCountDraft, setPlaceCountDraft] = useState<{
    classId: string
    placeCount: number | null
  } | null>(null)
  const handlePlaceCountChange = useCallback(
    async (classId: string, placeCount: number | null, classLabel: string) => {
      if (!ensureCanManageClasses()) return
      if (!showId) {
        toast.error('Select a show to update the places awarded.')
        return
      }
      setPlaceCountDraft({ classId, placeCount })
      try {
        await updateClassMutation.mutateAsync({ classId, showId, payload: { placeCount } })
        toast.success(
          placeCount === null
            ? `${classLabel} awards the show's places`
            : `${classLabel} awards ${formatPlaceCount(placeCount)}`
        )
      } catch (error) {
        toast.error(error instanceof Error ? error.message : 'Unable to update the places awarded.')
      } finally {
        setPlaceCountDraft(null)
      }
    },
    [ensureCanManageClasses, showId, updateClassMutation]
  )

  useEffect(() => {
    setLabelsEditor(prev => {
      const next: LabelsEditorState = {}
      for (const division of divisions) {
        for (const showClass of division.classes ?? []) {
          if (!showClass?.id) continue
          const resolved = resolveLabelsPerExhibitFromClass(showClass)
          const existing = prev[showClass.id]
          if (existing && existing.dirty) {
            next[showClass.id] = { ...existing, display: resolved }
          } else {
            next[showClass.id] = { value: String(resolved), dirty: false, display: resolved }
          }
        }
      }
      return next
    })
  }, [divisions])

  const allClasses = divisions.flatMap(division => division.classes)
  const catalogClassesQuery = useCatalogClasses({
    category: catalogFilters.category,
    q: catalogFilters.q,
  })
  const catalogClassList = catalogChoices(catalogClassesQuery.data ?? [])
  const existingCatalogClassIds = useMemo(() => {
    const ids = [...catalogClassList, ...bulkSelected.values()]
      .filter(choice => {
        const targetDivision =
          bulkForm.divisionMode === 'existing'
            ? bulkForm.existingDivisionId
            : divisions.find(
                division =>
                  division.code.toUpperCase() === `DIV-${choice.categoryCode.toUpperCase()}`
              )?.id
        return allClasses.some(
          showClass =>
            showClass.divisionId === targetDivision &&
            showClass.catalogClassId === choice.modelId &&
            (showClass.catalogSubtypeId ?? null) === (choice.catalogSubtypeId ?? null)
        )
      })
      .map(choice => choice.id)
    return new Set(ids)
  }, [
    allClasses,
    catalogClassList,
    bulkSelected,
    bulkForm.divisionMode,
    bulkForm.existingDivisionId,
    divisions,
  ])
  const isCatalogClassAvailable = useCallback(
    (catalogClassId: string) => !existingCatalogClassIds.has(catalogClassId),
    [existingCatalogClassIds]
  )
  const availableCatalogClassList = useMemo(
    () => catalogClassList.filter(catalogClass => isCatalogClassAvailable(catalogClass.id)),
    [catalogClassList, isCatalogClassAvailable]
  )
  const selectedBulkCatalogClasses = useMemo(
    () => Array.from(bulkSelected.values()),
    [bulkSelected]
  )
  const selectedBulkIds = useMemo(() => new Set(Array.from(bulkSelected.keys())), [bulkSelected])
  const bulkSelectionCount = selectedBulkCatalogClasses.length
  const allVisibleSelected =
    availableCatalogClassList.length > 0 &&
    availableCatalogClassList.every(catalogClass => selectedBulkIds.has(catalogClass.id))
  const editingClass = selectedClassId
    ? allClasses.find(item => item.id === selectedClassId)
    : undefined
  const catalogUsageQuery = useCatalogClassUsage(editingClass?.catalogClassId ?? undefined, {
    enabled: editSheetOpen && Boolean(editingClass?.catalogClassId),
  })
  const editingClassLabel = editingClass?.name ?? editingClass?.code ?? 'class'
  const editingClassExcluded = editingClass?.grandChampionEligible === false
  const editingExcludeId = editingClass?.id
    ? `edit-class-${editingClass.id}-exclude-grand`
    : 'edit-class-exclude-grand'
  const editExcludeDisabled =
    !editingClass?.id || !showId || classPermissionDisabled || updateClassMutation.isPending
  const showPlaceCount = currentShow?.defaultPlaceCount ?? DEFAULT_PLACE_COUNT
  const editingPlaceCount =
    placeCountDraft && placeCountDraft.classId === editingClass?.id
      ? placeCountDraft.placeCount
      : (editingClass?.placeCount ?? null)
  const editingPlaceCountId = editingClass?.id
    ? `edit-class-${editingClass.id}-place-count`
    : 'edit-class-place-count'

  const catalogUsageGroups = useMemo<CatalogUsageGroup[]>(() => {
    const usage = catalogUsageQuery.data
    if (!usage) return []
    const groups = new Map<string, CatalogUsageGroup>()

    usage.instances.forEach(instance => {
      const existing = groups.get(instance.show.id)
      if (existing) {
        existing.entries.push({
          division: instance.division,
          class: instance.class,
          isCurrentClass: instance.class.id === editingClass?.id,
        })
      } else {
        groups.set(instance.show.id, {
          show: instance.show,
          isCurrentShow: instance.show.id === currentShow?.id,
          entries: [
            {
              division: instance.division,
              class: instance.class,
              isCurrentClass: instance.class.id === editingClass?.id,
            },
          ],
        })
      }
    })

    const sorted = Array.from(groups.values())
    sorted.sort((a, b) => {
      const aDate = Date.parse(a.show.startDate)
      const bDate = Date.parse(b.show.startDate)
      if (!Number.isNaN(aDate) && !Number.isNaN(bDate) && aDate !== bDate) {
        return bDate - aDate
      }
      return a.show.name.localeCompare(b.show.name)
    })

    return sorted
  }, [catalogUsageQuery.data, editingClass?.id, currentShow?.id])

  const catalogUsageTotals = catalogUsageQuery.data?.totals
  const catalogUsageOtherShows = catalogUsageGroups.filter(group => !group.isCurrentShow).length

  const handleLabelsChange = (classId: string, value: string, resolved: number) => {
    setLabelsEditor(prev => {
      const existing = prev[classId]
      const display = existing?.display ?? resolved
      return {
        ...prev,
        [classId]: {
          value,
          dirty: true,
          display,
        },
      }
    })
  }

  const handleLabelsSubmit = async (showClass: TClass, resolved: number) => {
    if (!showId) {
      toast.error('Select a show before updating labels per exhibit.')
      return
    }
    if (!showClass.id) {
      toast.error('Cannot update labels for an unsaved class.')
      return
    }
    if (!ensureCanManageClasses()) return

    const editorEntry = labelsEditor[showClass.id]
    const currentDisplay = editorEntry?.display ?? resolved
    const rawValue = editorEntry?.value ?? String(resolved)
    const trimmed = rawValue.trim()
    const parsedValue = Number.parseInt(trimmed, 10)
    const isValid = trimmed.length > 0 && Number.isFinite(parsedValue) && parsedValue >= 1

    if (!isValid) {
      toast.error('Enter a label count of at least 1.')
      return
    }

    if (parsedValue === currentDisplay) {
      setLabelsEditor(prev => ({
        ...prev,
        [showClass.id!]: {
          value: String(currentDisplay),
          dirty: false,
          display: currentDisplay,
        },
      }))
      return
    }

    const previousState = editorEntry ?? {
      value: String(currentDisplay),
      dirty: false,
      display: currentDisplay,
    }

    const parameters = toParametersRecord(showClass.parameters)
    const payload = {
      parameters: {
        ...parameters,
        labelsPerExhibit: parsedValue,
      },
    }

    setSavingClassId(showClass.id)
    setLabelsEditor(prev => ({
      ...prev,
      [showClass.id!]: {
        value: String(parsedValue),
        dirty: false,
        display: parsedValue,
      },
    }))

    try {
      await updateClassMutation.mutateAsync({
        classId: showClass.id,
        showId,
        payload,
      })
      const classLabel = showClass.name ?? showClass.code ?? 'class'
      toast.success(`Saved labels required for ${classLabel}.`)
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : 'Failed to update labels required. Try again.'
      )
      setLabelsEditor(prev => ({
        ...prev,
        [showClass.id!]: previousState,
      }))
    } finally {
      setSavingClassId(null)
    }
  }

  useEffect(() => {
    setBulkSelected(prev => {
      const next = new Map(prev)
      let changed = false
      for (const id of prev.keys()) {
        if (!isCatalogClassAvailable(id)) {
          next.delete(id)
          changed = true
        }
      }
      return changed ? next : prev
    })
  }, [isCatalogClassAvailable])

  useEffect(() => {
    if (!editSheetOpen) {
      loadedOverride.current = { id: null, values: defaultOverrideForm }
      setSelectedClassId(null)
      setOverrideForm(defaultOverrideForm)
      setOverrideError(null)
    }
  }, [editSheetOpen])

  useEffect(() => {
    if (!editSheetOpen || !editingClass) return
    const previous = loadedOverride.current
    const next: OverrideFormState = {
      displayName: editingClass.displayName ?? '',
      ruleOverrides: isRecord(editingClass.ruleOverrides)
        ? JSON.stringify(editingClass.ruleOverrides, null, 2)
        : '',
      parameters: isRecord(editingClass.parameters)
        ? JSON.stringify(editingClass.parameters, null, 2)
        : '',
    }
    setOverrideForm(draft => {
      if (previous.id !== editingClass.id) return next
      return {
        displayName:
          draft.displayName === previous.values.displayName ? next.displayName : draft.displayName,
        ruleOverrides:
          draft.ruleOverrides === previous.values.ruleOverrides
            ? next.ruleOverrides
            : draft.ruleOverrides,
        parameters:
          draft.parameters === previous.values.parameters ? next.parameters : draft.parameters,
      }
    })
    loadedOverride.current = { id: editingClass.id ?? null, values: next }
  }, [editSheetOpen, editingClass])

  const handleCopyCatalogClassId = useCallback(() => {
    if (!editingClass?.catalogClassId) return
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      navigator.clipboard
        .writeText(editingClass.catalogClassId)
        .then(() => toast.success('Catalog class ID copied'))
        .catch(() => toast.error('Unable to copy catalog class ID'))
    } else {
      toast.error('Clipboard not available')
    }
  }, [editingClass?.catalogClassId])

  const handleToggleBulkSheet = (open: boolean) => {
    if (open && !ensureCanManageClasses()) {
      return
    }
    setBulkSheetOpen(open)
    const divisionMode = divisions.length > 0 ? 'existing' : 'new'
    const baseForm: BulkFormState = {
      ...defaultBulkForm,
      divisionMode,
      existingDivisionId: divisionMode === 'existing' ? divisions[0]?.id : undefined,
      newDivisionCode: generateDivisionCode(divisions),
      newDivisionName: generateDivisionName(divisions),
      newDivisionOrder: `${divisions.length + 1}`,
      codePrefix: '',
      codeStart: '1',
      namePrefix: '',
      nameSuffix: '',
    }

    if (open) {
      setBulkSelected(new Map())
      setBulkForm(baseForm)
      setBulkError(null)
      setBulkResults([])
      setBulkSubmitting(false)
    } else {
      setBulkSelected(new Map())
      setBulkForm(baseForm)
      setBulkError(null)
      setBulkResults([])
      setBulkSubmitting(false)
    }
  }

  const toggleBulkSelection = (catalogClass: CatalogChoice) => {
    if (!isCatalogClassAvailable(catalogClass.id)) return
    setBulkSelected(prev => {
      const next = new Map(prev)
      if (next.has(catalogClass.id)) {
        next.delete(catalogClass.id)
      } else {
        next.set(catalogClass.id, catalogClass)
      }
      return next
    })
  }

  const handleBulkSelectVisible = () => {
    if (availableCatalogClassList.length === 0) return
    const visibleIds = availableCatalogClassList.map(entry => entry.id)
    setBulkSelected(prev => {
      const next = new Map(prev)
      const everyVisibleSelected = visibleIds.every(id => next.has(id))
      if (everyVisibleSelected) {
        visibleIds.forEach(id => next.delete(id))
      } else {
        availableCatalogClassList.forEach(catalogClass => {
          if (isCatalogClassAvailable(catalogClass.id)) {
            next.set(catalogClass.id, catalogClass)
          }
        })
      }
      return next
    })
  }

  const handleOpenEditSheet = (classId: string, trigger: HTMLButtonElement) => {
    editTriggerRef.current = trigger
    setSelectedClassId(classId)
    setEditSheetOpen(true)
  }

  const handleBulkSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!showId) return
    if (!ensureCanManageClasses()) return

    if (selectedBulkCatalogClasses.length === 0) {
      setBulkError('Select at least one catalog class to add.')
      return
    }

    const useExistingDivision = bulkForm.divisionMode === 'existing'
    const existingDivisionId = useExistingDivision ? bulkForm.existingDivisionId?.trim() : undefined
    if (useExistingDivision && !existingDivisionId) {
      setBulkError('Choose an existing division or switch to creating a new one.')
      return
    }

    const codePrefix = bulkForm.codePrefix.trim()
    const codeStart = bulkForm.codeStart.trim()
    let codeSequence = 1
    if (codePrefix) {
      const parsedStart = Number.parseInt(codeStart || '1', 10)
      if (Number.isNaN(parsedStart)) {
        setBulkError('Starting number must be numeric when a code prefix is provided.')
        return
      }
      codeSequence = parsedStart > 0 ? parsedStart : 1
    }

    const namePrefix = bulkForm.namePrefix.trim()
    const nameSuffix = bulkForm.nameSuffix.trim()

    setBulkSubmitting(true)
    setBulkError(null)
    setBulkResults([])

    let targetDivisionId = useExistingDivision ? (existingDivisionId ?? null) : null
    const results: BulkAddResult[] = []

    const divisionPlans = new Map<string, DivisionPlan>()
    const existingDivisionByCode = new Map<string, TDivision>(
      divisions.map(division => [division.code.toUpperCase(), division])
    )
    const existingOrders = divisions.map(division => division.order)
    const usedDivisionOrders = new Set(existingOrders)
    let nextDivisionOrder = existingOrders.length > 0 ? Math.max(...existingOrders) + 1 : 1

    const categoryCodesInSelection = Array.from(
      new Set(
        selectedBulkCatalogClasses.map(catalogClass => catalogClass.categoryCode.toUpperCase())
      )
    )
    categoryCodesInSelection.sort((a, b) => {
      const aOrder = CATEGORY_ORDER.get(a) ?? Number.MAX_SAFE_INTEGER
      const bOrder = CATEGORY_ORDER.get(b) ?? Number.MAX_SAFE_INTEGER
      if (aOrder !== bOrder) return aOrder - bOrder
      return a.localeCompare(b)
    })

    const assignedOrderByCategory = new Map<string, number>()
    for (const categoryCode of categoryCodesInSelection) {
      const defaultDivisionCode = `DIV-${categoryCode}`
      if (existingDivisionByCode.has(defaultDivisionCode.toUpperCase())) continue
      let order = nextDivisionOrder
      while (usedDivisionOrders.has(order)) {
        order += 1
      }
      assignedOrderByCategory.set(categoryCode, order)
      usedDivisionOrders.add(order)
      nextDivisionOrder = order + 1
    }

    const ensureDivisionPlan = (
      catalogClass: CatalogChoice
    ): { key: string; plan: DivisionPlan } => {
      const categoryCode = catalogClass.categoryCode.toUpperCase()
      if (divisionPlans.has(categoryCode)) {
        return { key: categoryCode, plan: divisionPlans.get(categoryCode)! }
      }
      const defaultDivisionCode = `DIV-${categoryCode}`
      const defaultDivisionName = catalogClass.categoryName ?? `Division ${categoryCode}`
      const existingDivision = existingDivisionByCode.get(defaultDivisionCode.toUpperCase())
      let plan: DivisionPlan
      if (existingDivision) {
        plan = {
          divisionId: existingDivision.id,
          divisionCode: existingDivision.code,
          divisionName: existingDivision.name,
          divisionOrder: existingDivision.order,
        }
      } else {
        let divisionOrder = assignedOrderByCategory.get(categoryCode)
        if (divisionOrder === undefined) {
          let order = nextDivisionOrder
          while (usedDivisionOrders.has(order)) {
            order += 1
          }
          divisionOrder = order
          assignedOrderByCategory.set(categoryCode, divisionOrder)
          usedDivisionOrders.add(divisionOrder)
          nextDivisionOrder = divisionOrder + 1
        }
        plan = {
          divisionCode: defaultDivisionCode,
          divisionName: defaultDivisionName,
          divisionOrder,
        }
      }
      divisionPlans.set(categoryCode, plan)
      return { key: categoryCode, plan }
    }

    for (const catalogClass of selectedBulkCatalogClasses) {
      const payload: AddCatalogClassToShowInput = {
        catalogClassId: catalogClass.modelId,
        catalogSubtypeId: catalogClass.catalogSubtypeId,
      }

      let planKey: string | null = null
      if (useExistingDivision) {
        if (targetDivisionId) {
          payload.divisionId = targetDivisionId
        }
      } else {
        const { key, plan } = ensureDivisionPlan(catalogClass)
        planKey = key
        if (plan.divisionId) {
          payload.divisionId = plan.divisionId
        } else {
          payload.divisionCode = plan.divisionCode
          payload.divisionName = plan.divisionName
          if (typeof plan.divisionOrder === 'number') {
            payload.divisionOrder = plan.divisionOrder
          }
        }
      }

      if (codePrefix) {
        payload.code = `${codePrefix}${codeSequence}`
      }

      if (namePrefix || nameSuffix) {
        payload.name = `${namePrefix}${catalogClass.name}${nameSuffix}`
      }

      try {
        const response = await bulkAddMutation.mutateAsync({ showId, payload })
        results.push({
          catalogClassId: catalogClass.id,
          catalogClassName: catalogClass.name,
          status: 'success',
        })
        if (useExistingDivision) {
          if (!targetDivisionId && response.division?.id) {
            targetDivisionId = response.division.id
          }
        } else if (planKey) {
          const existingPlan = divisionPlans.get(planKey)
          if (existingPlan && response.division) {
            divisionPlans.set(planKey, {
              divisionId: response.division.id,
              divisionCode: response.division.code,
              divisionName: response.division.name,
              divisionOrder: response.division.order,
            })
            existingDivisionByCode.set(response.division.code.toUpperCase(), response.division)
          }
        }
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Unable to add class'
        results.push({
          catalogClassId: catalogClass.id,
          catalogClassName: catalogClass.name,
          status: 'error',
          message,
        })
      }

      if (codePrefix) {
        codeSequence += 1
      }
    }

    setBulkResults(results)
    setBulkSubmitting(false)

    const successCount = results.filter(result => result.status === 'success').length
    const errorCount = results.length - successCount

    if (successCount > 0) {
      toast.success(
        `${successCount} catalog ${successCount === 1 ? 'class' : 'classes'} added to show`
      )
    }

    if (errorCount > 0) {
      toast.error(`${errorCount} ${errorCount === 1 ? 'class' : 'classes'} could not be added`)
      setBulkError('Some catalog classes could not be added. Review the details below.')
    } else {
      setBulkSheetOpen(false)
    }
  }

  const handleOverrideSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!selectedClassId || !showId) return
    if (!editingClass) return
    if (!ensureCanManageClasses()) return

    setOverrideError(null)

    let parsedOverrides: Record<string, unknown> | null = null
    if (overrideForm.ruleOverrides.trim()) {
      const parsed = safeJsonParse<unknown>(overrideForm.ruleOverrides, null)
      if (parsed === null || !isRecord(parsed)) {
        setOverrideError('Overrides must be valid JSON object.')
        return
      }
      parsedOverrides = parsed
    }

    let parsedParameters: Record<string, unknown> | null = null
    if (overrideForm.parameters.trim()) {
      const parsed = safeJsonParse<unknown>(overrideForm.parameters, null)
      if (parsed === null || !isRecord(parsed)) {
        setOverrideError('Parameters must be valid JSON object.')
        return
      }
      parsedParameters = parsed
    }

    try {
      await updateClassMutation.mutateAsync({
        classId: selectedClassId,
        showId,
        payload: {
          displayName: overrideForm.displayName || null,
          ruleOverrides: parsedOverrides ?? null,
          parameters: parsedParameters ?? null,
        },
      })
      toast.success('Class overrides updated')
      setEditSheetOpen(false)
    } catch (error) {
      setOverrideError(error instanceof Error ? error.message : 'Unable to update class overrides')
    }
  }

  const handleResetOverrides = () => {
    setOverrideForm(defaultOverrideForm)
  }

  if (!currentShow) {
    return (
      <SelectShowNotice description="Choose a show to manage its classes and apply catalog references." />
    )
  }

  return (
    <div className="@container/show-class-page min-w-0 space-y-4 sm:space-y-6">
      {!permissionLoading && !canManageClasses && (
        <div className="rounded-md border border-dashed border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
          Managing classes requires {classRolesLabel}.
        </div>
      )}

      <div className="flex flex-col gap-3 sm:gap-4 @4xl/show-class-page:flex-row @4xl/show-class-page:items-start @4xl/show-class-page:justify-between">
        <div className="min-w-0">
          <h1 className="text-2xl font-semibold tracking-tight">Show Classes</h1>
          <CompactShowSummary show={currentShow} className="mt-1" />
        </div>
        <div className="flex flex-col gap-2 sm:gap-3 @sm/show-class-page:flex-row @sm/show-class-page:flex-wrap @4xl/show-class-page:shrink-0">
          {currentShow.seriesMembership?.series && permissions.can('reports:results') && (
            <Button asChild variant="outline" className="min-h-11">
              <Link to={`/show-series/${currentShow.seriesMembership.series.id}/alignment`}>
                Match classes across years
              </Link>
            </Button>
          )}
          {hasReindexAccess && (
            <div className="space-y-1">
              <Button
                data-testid="reindex-class-codes-button"
                className="min-h-11 w-full"
                variant="outline"
                disabled={reindexButtonDisabled}
                title={reindexButtonTitle}
                onClick={handleReindexCodes}
              >
                {reindexMutation.isPending ? 'Reindexing…' : 'Reindex Class Codes'}
              </Button>
              {reindexButtonTitle && (
                <p className="text-xs text-muted-foreground">
                  Only available while show is in Draft.
                </p>
              )}
            </div>
          )}
          <Button
            data-testid="bulk-add-button"
            className="min-h-11"
            onClick={() => handleToggleBulkSheet(true)}
            disabled={classPermissionDisabled}
            title={classPermissionReason}
          >
            Bulk Add From Catalog
          </Button>
        </div>
      </div>

      <div className="space-y-4">
        {divisionsQuery.isLoading && (
          <p className="text-sm text-muted-foreground">Loading divisions…</p>
        )}
        {divisionsQuery.isError && !divisionsQuery.data && (
          <p role="alert" className="text-sm text-destructive">
            Unable to load divisions for this show. Try refreshing or confirm you have access.
          </p>
        )}
        {divisionsQuery.data && divisions.length === 0 && (
          <Card className="border-dashed">
            <CardHeader>
              <CardTitle className="text-base">No divisions yet</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm text-muted-foreground">
              <p>
                Add your first division by bringing in a catalog class or creating a division
                manually.
              </p>
              <Button
                data-testid="bulk-add-empty-state"
                className="min-h-11"
                size="sm"
                onClick={() => handleToggleBulkSheet(true)}
              >
                Bulk add from catalog
              </Button>
            </CardContent>
          </Card>
        )}

        <ShowClassesOutline
          key={showId}
          divisions={divisions}
          show={currentShow}
          labelsEditor={labelsEditor}
          savingClassId={savingClassId}
          classDisabled={classPermissionDisabled}
          classReason={classPermissionReason}
          resultsDisabled={resultsPermissionDisabled}
          resultsReason={resultsPermissionReason}
          updatingClass={updateClassMutation.isPending}
          updatingResults={updateClassFinalization.isPending}
          onLabelsChange={handleLabelsChange}
          onLabelsSave={(showClass, resolved) => {
            void handleLabelsSubmit(showClass, resolved)
          }}
          onGrandChampionChange={(id, excluded, label) => {
            void handleToggleGrandChampionEligibility(id, excluded, label)
          }}
          onResultsChange={handleUpdateFinalization}
          onEdit={handleOpenEditSheet}
        />
      </div>

      <Sheet open={bulkSheetOpen} onOpenChange={handleToggleBulkSheet}>
        <SheetContent className="w-full sm:max-w-3xl">
          <form onSubmit={handleBulkSubmit} className="flex h-full flex-col">
            <SheetHeader>
              <SheetTitle>Bulk add catalog classes</SheetTitle>
              <SheetDescription>
                Select multiple catalog classes and add them to a division in one action.
              </SheetDescription>
            </SheetHeader>

            <div className="flex-1 space-y-6 overflow-y-auto px-4">
              <div className="grid gap-4 rounded-lg border p-4">
                <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                  <div className="flex items-center gap-2">
                    <label className="text-sm font-medium">Category</label>
                    <div className="flex flex-wrap gap-2">
                      {CATALOG_CATEGORY_CODES.map(category => (
                        <Button
                          key={category}
                          variant={catalogFilters.category === category ? 'default' : 'outline'}
                          size="sm"
                          type="button"
                          onClick={() =>
                            setCatalogFilters(prev => ({
                              ...prev,
                              category: prev.category === category ? undefined : category,
                            }))
                          }
                        >
                          {category}
                        </Button>
                      ))}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Input
                      placeholder="Search catalog"
                      value={catalogFilters.q ?? ''}
                      onChange={event =>
                        setCatalogFilters(prev => ({ ...prev, q: event.target.value || undefined }))
                      }
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setCatalogFilters({})}
                    >
                      Reset
                    </Button>
                  </div>
                </div>

                <div className="flex items-center justify-between text-sm text-muted-foreground">
                  <span>
                    {bulkSelectionCount === 0
                      ? 'No catalog classes selected.'
                      : `${bulkSelectionCount} ${bulkSelectionCount === 1 ? 'class' : 'classes'} selected.`}
                  </span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={handleBulkSelectVisible}
                    disabled={availableCatalogClassList.length === 0}
                  >
                    {allVisibleSelected
                      ? 'Clear visible'
                      : `Select all ${availableCatalogClassList.length}`}
                  </Button>
                </div>

                <div className="max-h-[260px] overflow-y-auto rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[48px]">Select</TableHead>
                        <TableHead>Name</TableHead>
                        <TableHead>Category</TableHead>
                        <TableHead>Description</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {catalogClassList.map(catalogClass => {
                        const available = isCatalogClassAvailable(catalogClass.id)
                        const isSelected = selectedBulkIds.has(catalogClass.id)
                        return (
                          <TableRow
                            key={catalogClass.id}
                            className={isSelected ? 'bg-muted/40' : undefined}
                            data-testid={`bulk-catalog-option-${catalogClass.id}`}
                          >
                            <TableCell>
                              <input
                                type="checkbox"
                                className="h-4 w-4 rounded border"
                                checked={isSelected}
                                onChange={() => toggleBulkSelection(catalogClass)}
                                aria-label={`Select ${catalogClass.name}`}
                                disabled={!available}
                              />
                            </TableCell>
                            <TableCell className="font-medium">
                              {catalogClass.name}
                              {!available && (
                                <span className="ml-2 text-xs text-muted-foreground">
                                  (already added)
                                </span>
                              )}
                            </TableCell>
                            <TableCell>
                              <Badge
                                variant="secondary"
                                className={getCategoryBadgeClass(catalogClass.categoryCode)}
                              >
                                {catalogClass.categoryCode}
                              </Badge>
                            </TableCell>
                            <TableCell className="max-w-[260px] truncate text-sm text-muted-foreground">
                              {catalogClass.description ?? '—'}
                            </TableCell>
                          </TableRow>
                        )
                      })}
                      {catalogClassesQuery.isFetching && (
                        <TableRow>
                          <TableCell
                            colSpan={4}
                            className="py-6 text-center text-sm text-muted-foreground"
                          >
                            Loading catalog classes…
                          </TableCell>
                        </TableRow>
                      )}
                      {!catalogClassesQuery.isFetching && catalogClassList.length === 0 && (
                        <TableRow>
                          <TableCell
                            colSpan={4}
                            className="py-6 text-center text-sm text-muted-foreground"
                          >
                            No catalog classes match your filters.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>

                {bulkSelectionCount > 0 && (
                  <div className="space-y-1 text-sm text-muted-foreground">
                    <h3 className="text-sm font-semibold text-muted-foreground">
                      Selected classes
                    </h3>
                    <ul className="list-disc space-y-1 pl-5">
                      {selectedBulkCatalogClasses.map(catalogClass => (
                        <li key={catalogClass.id}>{catalogClass.name}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              <Separator />

              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-muted-foreground">Division placement</h3>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant={bulkForm.divisionMode === 'existing' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() =>
                      setBulkForm(prev => ({
                        ...prev,
                        divisionMode: 'existing',
                        existingDivisionId: prev.existingDivisionId ?? divisions[0]?.id,
                      }))
                    }
                  >
                    Existing division
                  </Button>
                  <Button
                    type="button"
                    variant={bulkForm.divisionMode === 'new' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() =>
                      setBulkForm(prev => ({
                        ...prev,
                        divisionMode: 'new',
                        newDivisionCode:
                          prev.newDivisionCode.trim() || generateDivisionCode(divisions),
                        newDivisionName:
                          prev.newDivisionName.trim() || generateDivisionName(divisions),
                        newDivisionOrder: prev.newDivisionOrder.trim() || `${divisions.length + 1}`,
                      }))
                    }
                  >
                    New division
                  </Button>
                </div>

                {bulkForm.divisionMode === 'existing' ? (
                  <div className="grid gap-2">
                    <Label htmlFor={bulkFormIds.existingDivision}>Choose division</Label>
                    <select
                      id={bulkFormIds.existingDivision}
                      className="flex h-10 rounded-md border bg-background px-3 text-sm"
                      value={bulkForm.existingDivisionId ?? ''}
                      onChange={event =>
                        setBulkForm(prev => ({
                          ...prev,
                          existingDivisionId: event.target.value || undefined,
                        }))
                      }
                    >
                      <option value="">Select a division…</option>
                      {divisions.map(division => (
                        <option key={division.id} value={division.id}>
                          {formatDivisionLabel(division)}
                        </option>
                      ))}
                    </select>
                  </div>
                ) : (
                  <div className="rounded-md border border-dashed p-4 text-sm text-muted-foreground">
                    Default divisions are created automatically from each catalog class’s category
                    (for example, “Extracted & Comb Honey (Category H)” with code “DIV-H”). You can
                    rename or reorganize divisions after importing the classes.
                  </div>
                )}

                <Separator />

                <div className="grid gap-4 md:grid-cols-2">
                  <div className="grid gap-2">
                    <Label htmlFor={bulkFormIds.codePrefix}>Class code prefix</Label>
                    <Input
                      id={bulkFormIds.codePrefix}
                      value={bulkForm.codePrefix}
                      onChange={event =>
                        setBulkForm(prev => ({ ...prev, codePrefix: event.target.value }))
                      }
                      placeholder="Optional e.g. CL-"
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor={bulkFormIds.codeStart}>Starting number</Label>
                    <Input
                      id={bulkFormIds.codeStart}
                      value={bulkForm.codeStart}
                      onChange={event =>
                        setBulkForm(prev => ({ ...prev, codeStart: event.target.value }))
                      }
                      placeholder="1"
                      disabled={!bulkForm.codePrefix.trim()}
                    />
                    <p className="text-xs text-muted-foreground">
                      Applies when a code prefix is provided.
                    </p>
                  </div>
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <div className="grid gap-2">
                    <Label htmlFor={bulkFormIds.namePrefix}>Class name prefix</Label>
                    <Input
                      id={bulkFormIds.namePrefix}
                      value={bulkForm.namePrefix}
                      onChange={event =>
                        setBulkForm(prev => ({ ...prev, namePrefix: event.target.value }))
                      }
                      placeholder="Optional prefix"
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor={bulkFormIds.nameSuffix}>Class name suffix</Label>
                    <Input
                      id={bulkFormIds.nameSuffix}
                      value={bulkForm.nameSuffix}
                      onChange={event =>
                        setBulkForm(prev => ({ ...prev, nameSuffix: event.target.value }))
                      }
                      placeholder="Optional suffix"
                    />
                  </div>
                </div>

                {bulkError && <p className="text-sm text-destructive">{bulkError}</p>}

                {bulkResults.length > 0 && (
                  <div className="rounded-lg border p-4">
                    <h3 className="text-sm font-semibold text-muted-foreground">
                      Latest bulk add results
                    </h3>
                    <ul className="mt-2 space-y-2 text-sm">
                      {bulkResults.map(result => (
                        <li
                          key={result.catalogClassId}
                          className={
                            result.status === 'success' ? 'text-emerald-600' : 'text-destructive'
                          }
                        >
                          <span className="font-medium">{result.catalogClassName}</span>
                          {result.status === 'success'
                            ? ' — Added'
                            : ` — ${result.message ?? 'Unable to add class'}`}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>

            <SheetFooter>
              <div className="flex flex-col gap-2">
                <Button
                  type="submit"
                  disabled={bulkSubmitting || classPermissionDisabled}
                  title={classPermissionReason}
                >
                  {bulkSubmitting ? 'Adding…' : 'Add selected classes'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => handleToggleBulkSheet(false)}
                  disabled={bulkSubmitting}
                >
                  Cancel
                </Button>
              </div>
            </SheetFooter>
          </form>
        </SheetContent>
      </Sheet>

      <Sheet open={editSheetOpen} onOpenChange={setEditSheetOpen}>
        <SheetContent
          className="w-full sm:max-w-2xl"
          onCloseAutoFocus={event => {
            if (editTriggerRef.current?.isConnected) {
              event.preventDefault()
              editTriggerRef.current.focus()
            }
          }}
        >
          <form onSubmit={handleOverrideSubmit} className="flex h-full flex-col">
            <SheetHeader>
              <SheetTitle>Class overrides</SheetTitle>
              <SheetDescription>
                Customize how this class appears in the show and adjust rule details to reflect
                local requirements.
              </SheetDescription>
            </SheetHeader>

            {editingClass ? (
              <div className="flex-1 space-y-6 overflow-y-auto px-4">
                <div className="grid gap-4 rounded-lg border p-4">
                  <div>
                    <h3 className="text-base font-semibold">{editingClass.name}</h3>
                    <p className="text-sm text-muted-foreground">Code: {editingClass.code}</p>
                  </div>
                  {editingClass.catalogClass && (
                    <div className="text-sm text-muted-foreground">
                      <p className="font-medium">Catalog: {editingClass.catalogClass.name}</p>
                      <p>{editingClass.catalogClass.categoryName}</p>
                    </div>
                  )}
                </div>

                <ModelDefaultsReview
                  key={editingClass.id}
                  klass={editingClass}
                  showId={showId!}
                  enabled={!classPermissionDisabled && showIsDraft}
                />

                <div className="space-y-2 rounded-lg border p-4">
                  <div className="flex items-center gap-2">
                    <input
                      id={editingExcludeId}
                      type="checkbox"
                      className="h-4 w-4 rounded border"
                      checked={editingClassExcluded}
                      onChange={event => {
                        if (!editingClass?.id) return
                        void handleToggleGrandChampionEligibility(
                          editingClass.id,
                          event.target.checked,
                          editingClassLabel
                        )
                      }}
                      disabled={editExcludeDisabled}
                      title={
                        classPermissionDisabled && classPermissionReason
                          ? classPermissionReason
                          : undefined
                      }
                      aria-label={`Exclude ${editingClassLabel} from Grand Champion`}
                    />
                    <Label
                      htmlFor={editingExcludeId}
                      className="cursor-pointer text-sm font-medium"
                    >
                      Exclude from Grand Champion
                    </Label>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {editingClassExcluded
                      ? 'Grand Champion leaderboards and labels will ignore this class.'
                      : 'Eligible classes remain in Grand Champion leaderboards and labels.'}
                  </p>
                </div>

                <div className="space-y-2 rounded-lg border p-4">
                  <Label htmlFor={editingPlaceCountId} className="text-sm font-medium">
                    Places awarded
                  </Label>
                  <Select
                    value={editingPlaceCount == null ? 'show' : String(editingPlaceCount)}
                    onValueChange={value => {
                      if (!editingClass?.id) return
                      void handlePlaceCountChange(
                        editingClass.id,
                        value === 'show' ? null : Number(value),
                        editingClassLabel
                      )
                    }}
                    disabled={editExcludeDisabled}
                  >
                    <SelectTrigger
                      id={editingPlaceCountId}
                      className="w-full sm:w-64"
                      title={
                        classPermissionDisabled && classPermissionReason
                          ? classPermissionReason
                          : undefined
                      }
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="show">
                        Show default ({formatPlaceCount(showPlaceCount)})
                      </SelectItem>
                      {CLASS_PLACEMENT_KEYS.map((place, index) => (
                        <SelectItem key={place} value={String(index + 1)}>
                          {formatPlaceRange(index + 1)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Judging shows this many placements for the class. Most classes use the show
                    default; a class such as Black Jar can award a different number.
                  </p>
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-3">
                    <h3 className="text-sm font-semibold text-muted-foreground">Adopted rules</h3>
                    <RulesViewer
                      rules={editingClass.catalogClass?.rules ?? editingClass.rules ?? {}}
                      emptyFallback="No catalog rules available"
                    />
                  </div>
                  <div className="space-y-3">
                    <h3 className="text-sm font-semibold text-muted-foreground">Effective rules</h3>
                    <RulesViewer
                      rules={mergeCatalogRules(
                        editingClass.catalogClass?.rules ?? editingClass.rules ?? {},
                        editingClass.ruleOverrides ?? {}
                      )}
                    />
                  </div>
                </div>

                {editingClass.catalogClassId && (
                  <div className="space-y-3 rounded-lg border p-4">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div>
                        <h3 className="text-sm font-semibold text-muted-foreground">
                          Cross-show alignment
                        </h3>
                        <p className="text-xs text-muted-foreground">
                          Catalog class{' '}
                          <span className="font-mono text-[11px]">
                            {editingClass.catalogClassId}
                          </span>
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        {catalogUsageTotals && (
                          <Badge variant="outline">
                            {catalogUsageTotals.shows} show
                            {catalogUsageTotals.shows === 1 ? '' : 's'}
                          </Badge>
                        )}
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={handleCopyCatalogClassId}
                        >
                          Copy ID
                        </Button>
                      </div>
                    </div>
                    {catalogUsageQuery.isLoading ? (
                      <p className="text-sm text-muted-foreground">Loading cross-show usage…</p>
                    ) : catalogUsageQuery.isError ? (
                      <p className="text-sm text-destructive">
                        {catalogUsageQuery.error?.message ?? 'Unable to load cross-show usage.'}
                      </p>
                    ) : catalogUsageGroups.length === 0 ? (
                      <p className="text-sm text-muted-foreground">
                        This catalog class is not linked to any other shows yet.
                      </p>
                    ) : (
                      <div className="space-y-3">
                        {catalogUsageGroups.map(group => (
                          <div key={group.show.id} className="rounded-md border border-dashed p-3">
                            <div className="flex flex-wrap items-center justify-between gap-2">
                              <div>
                                <p className="font-medium">{group.show.name}</p>
                                <p className="text-xs text-muted-foreground">
                                  {formatIsoDate(group.show.startDate)} · {group.show.status}
                                </p>
                              </div>
                              <div className="flex items-center gap-2">
                                {group.isCurrentShow && (
                                  <Badge variant="secondary">Current show</Badge>
                                )}
                                <Badge variant="outline">
                                  {group.entries.length} class
                                  {group.entries.length === 1 ? '' : 'es'}
                                </Badge>
                              </div>
                            </div>
                            <ul className="mt-2 space-y-1 text-sm">
                              {group.entries.map(entry => (
                                <li
                                  key={entry.class.id}
                                  className="flex flex-wrap items-center justify-between gap-2 rounded-sm bg-muted/40 px-2 py-1"
                                >
                                  <div>
                                    <span className="font-medium">{entry.class.name}</span>
                                    <span className="ml-2 font-mono text-xs text-muted-foreground">
                                      {entry.class.code}
                                    </span>
                                    <span className="ml-2 text-xs text-muted-foreground">
                                      {entry.division.name}
                                    </span>
                                  </div>
                                  {entry.isCurrentClass && (
                                    <Badge variant="outline">This class</Badge>
                                  )}
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                    )}
                    {catalogUsageOtherShows > 0 && (
                      <p className="text-xs text-muted-foreground">
                        Also linked in {catalogUsageOtherShows}{' '}
                        {catalogUsageOtherShows === 1 ? 'other show' : 'other shows'}.
                      </p>
                    )}
                  </div>
                )}

                <Separator />

                <div className="grid gap-3">
                  <div className="grid gap-2">
                    <Label htmlFor={overrideFieldIds.displayName}>Display name</Label>
                    <Input
                      id={overrideFieldIds.displayName}
                      value={overrideForm.displayName}
                      onChange={event =>
                        setOverrideForm(prev => ({ ...prev, displayName: event.target.value }))
                      }
                      placeholder="Optional display name shown to exhibitors"
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor={overrideFieldIds.ruleOverrides}>Rule overrides JSON</Label>
                    <textarea
                      id={overrideFieldIds.ruleOverrides}
                      value={overrideForm.ruleOverrides}
                      onChange={event =>
                        setOverrideForm(prev => ({ ...prev, ruleOverrides: event.target.value }))
                      }
                      className="min-h-[160px] rounded-md border bg-background px-3 py-2 text-sm font-mono"
                      placeholder='e.g. {"labels": {"placement": "front"}}'
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor={overrideFieldIds.parameters}>Parameters JSON</Label>
                    <textarea
                      id={overrideFieldIds.parameters}
                      value={overrideForm.parameters}
                      onChange={event =>
                        setOverrideForm(prev => ({ ...prev, parameters: event.target.value }))
                      }
                      className="min-h-[120px] rounded-md border bg-background px-3 py-2 text-sm font-mono"
                      placeholder='e.g. {"theme": "Wildflower"}'
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleResetOverrides}
                    >
                      Reset fields
                    </Button>
                  </div>
                </div>

                {overrideError && <p className="text-sm text-destructive">{overrideError}</p>}
              </div>
            ) : (
              <div className="flex-1 px-4">
                <p className="text-sm text-muted-foreground">
                  Select a class to view and edit overrides.
                </p>
              </div>
            )}

            <SheetFooter>
              <div className="flex flex-col gap-2">
                <Button type="submit" disabled={updateClassMutation.isPending || !editingClass}>
                  {updateClassMutation.isPending ? 'Saving…' : 'Save overrides'}
                </Button>
                <Button type="button" variant="outline" onClick={() => setEditSheetOpen(false)}>
                  Cancel
                </Button>
              </div>
            </SheetFooter>
          </form>
        </SheetContent>
      </Sheet>
    </div>
  )
}
